/**
 * Activity time utilities for automatic activity selection
 * Supports development mock time via URL parameters
 */

/**
 * Get current time (supports mockTime in development)
 * Usage: ?mockTime=2026-03-07T10:00 or ?mockActivity=ntu2026
 * @param {array} activities - Activity options for mockActivity lookup
 * @returns {Date} Current time or mocked time
 */
export const getCurrentTime = (activities) => {
  if (process.env.NODE_ENV !== 'development') {
    return new Date();
  }

  const params = new URLSearchParams(window.location.search);

  // Option 1: Direct time specification
  const mockTime = params.get('mockTime');
  if (mockTime) {
    const parsed = new Date(mockTime);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  // Option 2: Activity code - use activity start time
  const mockActivity = params.get('mockActivity');
  if (mockActivity && activities) {
    const activity = activities.find(a => a.value === mockActivity);
    if (activity?.startTime) {
      return new Date(activity.startTime);
    }
  }

  return new Date();
};

/**
 * Find the currently active activity based on time
 * @param {array} activities - Activity options with startTime/endTime
 * @param {Date} currentTime - Current time to check against
 * @returns {object|null} Active activity or null
 */
export const getActiveActivity = (activities, currentTime) => {
  if (!activities || !currentTime) return null;

  return activities.find(activity => {
    if (!activity.startTime || !activity.endTime) return false;
    const start = new Date(activity.startTime);
    const end = new Date(activity.endTime);
    return currentTime >= start && currentTime <= end;
  }) || null;
};

/**
 * Check if an activity option is selectable
 * @param {object} activity - Activity option to check
 * @param {object|null} activeActivity - Currently active activity
 * @returns {boolean} Whether the activity is selectable
 */
export const isActivitySelectable = (activity, activeActivity) => {
  // No active activity → all options selectable
  if (!activeActivity) {
    return true;
  }
  // Active activity exists → only that activity is selectable
  return activity.value === activeActivity.value;
};

/**
 * Get displayable activities (filter out unconfirmed activities during non-event time)
 * @param {array} activities - All activity options
 * @param {object|null} activeActivity - Currently active activity
 * @returns {array} Activities to display
 */
export const getDisplayableActivities = (activities, activeActivity) => {
  if (!activities) return [];

  // During active event → show all activities (most will be disabled)
  if (activeActivity) {
    return activities;
  }

  // Non-event time → hide activities without confirmed time
  return activities.filter(a => a.startTime && a.endTime);
};
